import type { Location, MetaDescriptor } from "react-router";
import { brandName } from "@mealmanager/legal-content";

const SITE_NAME = brandName;
const DEFAULT_DESCRIPTION =
  `${brandName} helps you unprocess your plate with whole-food recipes, personalised meal plans, and an AI Sous Chef, so eating real food is simple.`;
const DEFAULT_IMAGE_PATH = "/logo-light.png";
const DEFAULT_IMAGE_ALT = `${brandName} logo`;
const DEFAULT_LOCALE = "en_US";

const PUBLIC_BASE_URL = (() => {
  if (typeof process !== "undefined" && typeof process.env?.PUBLIC_APP_URL === "string" && process.env.PUBLIC_APP_URL.length > 0) {
    return process.env.PUBLIC_APP_URL;
  }

  if (typeof import.meta !== "undefined" && typeof import.meta.env === "object") {
    const maybeViteValue =
      (import.meta.env as Record<string, string | undefined>)?.VITE_PUBLIC_APP_URL ??
      (import.meta.env as Record<string, string | undefined>)?.PUBLIC_APP_URL;
    if (typeof maybeViteValue === "string" && maybeViteValue.length > 0) {
      return maybeViteValue;
    }
  }

  return "https://realvore.com";
})();

const truncate = (value: string, maxLength = 155) => {
  if (value.length <= maxLength) {
    return value;
  }
  return `${value.slice(0, maxLength - 1).trimEnd()}…`;
};

const stripLineBreaks = (value: string) => value.replace(/\s+/g, " ").trim();

const ensureAbsoluteUrl = (target: string, baseUrl: string) => {
  try {
    return new URL(target, baseUrl).toString();
  } catch {
    try {
      return new URL(baseUrl).toString();
    } catch {
      return target;
    }
  }
};

export const getPublicBaseUrl = () => PUBLIC_BASE_URL;

export const buildCanonicalUrl = (
  location: Pick<Location, "pathname" | "search">,
  baseUrl: string = PUBLIC_BASE_URL
): string => {
  return ensureAbsoluteUrl(location.pathname || "/", baseUrl);
};

type SeoMetaOptions = {
  location: Location;
  title: string;
  description: string;
  type?: "website" | "article";
  image?: string | null;
  imageAlt?: string;
  publishedTime?: string | null;
  modifiedTime?: string | null;
  noindex?: boolean;
  follow?: boolean;
  includeCanonical?: boolean;
  ldJson?: Record<string, unknown>;
  links?: MetaDescriptor[];
  canonicalPath?: string;
};

export const buildSeoMeta = ({
  location,
  title,
  description,
  type = "website",
  image,
  imageAlt,
  publishedTime,
  modifiedTime,
  noindex = false,
  follow = false,
  includeCanonical = true,
  ldJson,
  links,
  canonicalPath,
}: SeoMetaOptions): MetaDescriptor[] => {
  const baseUrl = getPublicBaseUrl();
  const canonical = canonicalPath ? ensureAbsoluteUrl(canonicalPath, baseUrl) : buildCanonicalUrl(location, baseUrl);
  const pageUrl = includeCanonical ? canonical : ensureAbsoluteUrl(`${location.pathname}${location.search ?? ""}`, baseUrl);
  const normalizedDescription = truncate(stripLineBreaks(description || DEFAULT_DESCRIPTION));
  const normalizedImage = ensureAbsoluteUrl(image && image.length > 0 ? image : DEFAULT_IMAGE_PATH, baseUrl);
  const normalizedImageAlt = imageAlt || DEFAULT_IMAGE_ALT;

  const meta: MetaDescriptor[] = [
    { title },
    { name: "description", content: normalizedDescription },
    { name: "robots", content: noindex ? `noindex, ${follow ? "follow" : "nofollow"}` : "index, follow" },
    { name: "application-name", content: SITE_NAME },
    { property: "og:locale", content: DEFAULT_LOCALE },
    { property: "og:type", content: type },
    { property: "og:title", content: title },
    { property: "og:description", content: normalizedDescription },
    { property: "og:url", content: pageUrl },
    { property: "og:site_name", content: SITE_NAME },
    { property: "og:image", content: normalizedImage },
    { property: "og:image:alt", content: normalizedImageAlt },
    { name: "twitter:card", content: "summary_large_image" },
    { name: "twitter:title", content: title },
    { name: "twitter:description", content: normalizedDescription },
    { name: "twitter:image", content: normalizedImage },
    { name: "twitter:image:alt", content: normalizedImageAlt },
  ];

  if (includeCanonical) {
    meta.push({ tagName: "link", rel: "canonical", href: canonical });
  }

  if (publishedTime) {
    meta.push({ property: "article:published_time", content: publishedTime });
  }
  if (modifiedTime) {
    meta.push({ property: "article:modified_time", content: modifiedTime });
  }
  if (links) {
    meta.push(...links);
  }
  if (ldJson) {
    meta.push({ "script:ld+json": ldJson });
  }

  return meta;
};

export const defaultDescription = DEFAULT_DESCRIPTION;
export const siteName = SITE_NAME;
export const toAbsoluteUrl = (target: string, baseUrl = PUBLIC_BASE_URL) => ensureAbsoluteUrl(target, baseUrl);
